import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Paper,
  Divider,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Badge,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  LocalShipping as SupplierIcon,
  Build as RepairIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderType: 'supplier' | 'customer' | 'dealership' | 'repair-shop';
  content: string;
  timestamp: Date;
  read: boolean;
}

interface Contact {
  id: string;
  name: string;
  type: 'supplier' | 'customer' | 'dealership' | 'repair-shop';
  lastMessage: string;
  lastMessageTime: Date;
  unreadCount: number;
  online: boolean;
}

export const MessagesHub: React.FC = () => {
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);

  // Mock contacts data
  const contacts: Contact[] = [
    {
      id: '1',
      name: 'Parts Corp SA',
      type: 'supplier',
      lastMessage: 'Your order #12345 has been confirmed',
      lastMessageTime: new Date(2024, 2, 15, 10, 30),
      unreadCount: 2,
      online: true,
    },
    {
      id: '2',
      name: 'AutoFix Garage',
      type: 'repair-shop',
      lastMessage: 'Do you have Toyota Corolla brake pads in stock?',
      lastMessageTime: new Date(2024, 2, 15, 9, 45),
      unreadCount: 0,
      online: true,
    },
    {
      id: '3',
      name: 'Toyota Dealership Sandton',
      type: 'dealership',
      lastMessage: 'Requesting quote for bulk order',
      lastMessageTime: new Date(2024, 2, 14, 16, 20),
      unreadCount: 1,
      online: false,
    },
  ];

  // Mock messages data
  const messages: Message[] = [
    {
      id: '1',
      senderId: '1',
      senderName: 'Parts Corp SA',
      senderType: 'supplier',
      content: 'Your order #12345 has been confirmed',
      timestamp: new Date(2024, 2, 15, 10, 30),
      read: false,
    },
    {
      id: '2',
      senderId: 'me',
      senderName: 'Me',
      senderType: 'supplier',
      content: 'Thank you for the confirmation. When can I expect delivery?',
      timestamp: new Date(2024, 2, 15, 10, 31),
      read: true,
    },
    {
      id: '3',
      senderId: '1',
      senderName: 'Parts Corp SA',
      senderType: 'supplier',
      content: 'Delivery is scheduled for tomorrow between 9 AM and 12 PM',
      timestamp: new Date(2024, 2, 15, 10, 35),
      read: false,
    },
  ];

  const handleSendMessage = () => {
    if (messageText.trim()) {
      // Implement send message logic here
      setMessageText('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const getContactAvatar = (type: string) => {
    switch (type) {
      case 'supplier':
        return <SupplierIcon />;
      case 'dealership':
        return <BusinessIcon />;
      case 'repair-shop':
        return <RepairIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-ZA');
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Messages
        </Typography>

        <Grid container spacing={3}>
          {/* Contacts List */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: 'calc(100vh - 200px)' }}>
              <CardContent sx={{ p: 0, height: '100%' }}>
                <Box sx={{ p: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search contacts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Tabs
                  value={currentTab}
                  onChange={(e, newValue) => setCurrentTab(newValue)}
                  variant="fullWidth"
                >
                  <Tab label="All" />
                  <Tab label="Suppliers" />
                  <Tab label="Customers" />
                </Tabs>
                <List sx={{ overflow: 'auto', height: 'calc(100% - 120px)' }}>
                  {contacts.map((contact) => (
                    <ListItem
                      key={contact.id}
                      button
                      selected={selectedContact?.id === contact.id}
                      onClick={() => setSelectedContact(contact)}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        bgcolor: selectedContact?.id === contact.id ? 'action.selected' : 'transparent',
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          variant="dot"
                          color={contact.online ? 'success' : 'default'}
                        >
                          <Avatar>{getContactAvatar(contact.type)}</Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">{contact.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(contact.lastMessageTime)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '70%',
                              }}
                            >
                              {contact.lastMessage}
                            </Typography>
                            {contact.unreadCount > 0 && (
                              <Chip
                                size="small"
                                label={contact.unreadCount}
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Chat Area */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: 'calc(100vh - 200px)' }}>
              <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {selectedContact ? (
                  <>
                    {/* Chat Header */}
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }}>{getContactAvatar(selectedContact.type)}</Avatar>
                        <Box>
                          <Typography variant="h6">{selectedContact.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedContact.online ? 'Online' : 'Offline'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Messages */}
                    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                      {messages.map((message) => (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            justifyContent: message.senderId === 'me' ? 'flex-end' : 'flex-start',
                            mb: 2,
                          }}
                        >
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor: message.senderId === 'me' ? 'primary.main' : 'grey.100',
                              color: 'black',
                              maxWidth: '70%',
                            }}
                          >
                            <Typography variant="body1">{message.content}</Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                textAlign: 'right',
                                color: message.senderId === 'me' ? 'black' : 'text.secondary',
                              }}
                            >
                              {formatTime(message.timestamp)}
                            </Typography>
                          </Paper>
                        </Box>
                      ))}
                    </Box>

                    {/* Message Input */}
                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Grid container spacing={2}>
                        <Grid item xs>
                          <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            placeholder="Type a message..."
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton>
                                    <AttachFileIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <Button
                            variant="contained"
                            endIcon={<SendIcon />}
                            onClick={handleSendMessage}
                            disabled={!messageText.trim()}
                          >
                            Send
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      Select a contact to start messaging
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}; 